import {
  type DateTimeLike,
  formatClockTime,
  parseDateTimeSafe,
} from '@racemap/utilities/functions/datetime';
import { Typography } from 'antd';
import { DateTime } from 'luxon';
import { type CSSProperties, useMemo } from 'react';

interface Props {
  time: DateTimeLike | null | undefined;
  format?:
    | Intl.DateTimeFormatOptions
    | string
    | 'iso'
    | 'shortDate'
    | 'time'
    | 'dayWithTime'
    | 'shortTime'
    | 'longTime';
  zone?: string;
  color?: CSSProperties['color'];
  title?: string;
}

const { Text } = Typography;

export const Timestamp: React.FC<Props> = ({ time, format, zone, color, title }) => {
  const dateTime = parseDateTimeSafe(time, zone);
  const timestamp = useMemo(() => {
    if (dateTime == null) {
      return '';
    }
    if (format === 'iso') {
      return dateTime.toISO();
    }
    if (format === 'shortDate') {
      return dateTime.toLocaleString(DateTime.DATE_SHORT);
    }
    if (format === 'time') {
      return formatClockTime(dateTime);
    }
    if (format === 'dayWithTime') {
      return dateTime.toLocaleString({
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      });
    }
    if (format === 'shortTime') {
      return dateTime.toLocaleString(DateTime.TIME_24_SIMPLE);
    }
    if (format === 'longTime') {
      // Always three digits, and always present: these render in table columns and tooltip rows
      // that are scanned against each other, so the width must not depend on the value.
      return formatClockTime(dateTime, true);
    }

    if (typeof format === 'string') {
      return dateTime.toFormat(format);
    }

    if (format) {
      return dateTime.toLocaleString(format);
    }

    return dateTime.toLocaleString(DateTime.DATETIME_MED);
  }, [dateTime, format]);

  const defaultTitle = dateTime ? dateTime.toISO() : 'Invalid Date';

  return (
    <Text style={{ color }} title={title || defaultTitle || undefined}>
      {timestamp}
    </Text>
  );
};
